/**
 * restorationVsCorrectionEngine.js
 *
 * Determines the appropriate directional posture: is the response
 * primarily oriented toward restoration/support, or is correction
 * genuinely the right move?
 *
 * Correction is only appropriate when ALL of the following are true:
 *  — There is clarity (not ambiguity) about the problem
 *  — Risk is not minimal
 *  — The pattern has persisted
 *  — The pastor explicitly seeks direction (not just to process)
 *
 * Otherwise, restoration/accompaniment is always the safer inclination.
 *
 * Inclinations:
 *   restaurar   — orient toward wholeness without rushing
 *   sostener    — hold steady; primary need is presence, not direction
 *   exhortar    — gentle encouragement toward a step
 *   corregir    — clear correction is warranted (rare)
 *   esperar     — not the moment to act; watch and wait
 */

/**
 * @param {{
 *   ethics:    object,
 *   emotion:   object|null,
 *   riskLevel: string,
 *   intent:    string,
 *   text:      string,
 * }} params
 * @returns {{
 *   inclination: "restaurar"|"sostener"|"exhortar"|"corregir"|"esperar",
 *   instruction: string|null,
 * }}
 */
export function resolveRestorationVsCorrection({
  ethics,
  emotion,
  riskLevel = "none",
  intent,
  text,
}) {
  const detected = ethics?.detected ?? [];
  const dom      = emotion?.dominantEmotion ?? "none";
  const t        = text ?? "";

  // Fragile emotions → sostener first
  const fragileEmotions = ["grief", "shame", "crisis", "loneliness"];
  if (fragileEmotions.includes(dom)) {
    return {
      inclination: "sostener",
      instruction: "La persona necesita presencia y sostén antes que dirección. No intervengas con corrección ahora.",
    };
  }

  // Explicit correction request from pastor + clear ethics signal + medium+ risk
  const correctionRequested = /corregir|confrontar|disciplin|hablarle\s+claro|llamarle\s+la\s+atenci/i.test(t);
  const hasClearEthics      = detected.some((d) => ["discipline", "apostasy", "marital_crisis"].includes(d));
  const hasPersistence      = detected.includes("long_absence") || detected.includes("pattern");

  if (correctionRequested && hasClearEthics && (riskLevel === "high" || hasPersistence)) {
    return {
      inclination: "corregir",
      instruction:
        "La corrección pastoral es pertinente aquí. Sé claro y directo, pero sin dureza. " +
        "La claridad es un acto de amor, no de juicio.",
    };
  }

  // Gentle encouragement when planning + positive direction
  if (intent === "visit_plan" || intent === "family_followup") {
    if (riskLevel === "none" || riskLevel === "low") {
      return {
        inclination: "exhortar",
        instruction: null,
      };
    }
  }

  // Wait when urgencia_paciencia or spiritual_confusion + no request for action
  const wantToWait = /esperando|tiempo|proceso|no\s+s[eé]\s+cuándo/i.test(t);
  if (dom === "spiritual_confusion" || wantToWait) {
    return {
      inclination: "esperar",
      instruction: "El momento de actuar no ha llegado. Acompaña el proceso sin apresurarlo.",
    };
  }

  // Default: orient toward restoration
  if (hasClearEthics || riskLevel === "medium") {
    return {
      inclination: "restaurar",
      instruction: "Orienta hacia la restauración. El camino de regreso importa más que la velocidad.",
    };
  }

  return { inclination: "restaurar", instruction: null };
}
